import { Button } from "primereact/button";
import FormSubHeading from "../../components/FormSubHeading";
import { MultiSelect } from "primereact/multiselect";
import Dashboard from "../../Layout/Dashboard";
import { Checkbox } from "primereact/checkbox";
import { useForm, useStore } from "@tanstack/react-form";
import { useDiagnosisListCached } from "../../hooks/useDiagnosisListCached";
import { useContext, useState } from "react";
import RequirementModal from "../RequirementModalNew";
import { useNavigate, useParams } from "react-router-dom";
import { useSingleMedicalCase } from "../../hooks/queries";
import RequirementDataView from "../Listing/RequirementDataView";
import { ProgressSpinner } from "primereact/progressspinner";
import { ToastContext } from "../../ToastContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateCase } from "../../services/medicalCases";
import { CaseStatus } from "../../constants";

/**
 * Component for assigning a case request
 * @author gabrielm
 */
export default function AssignCaseRequest() {
  // Buscar detalle del caso
  const { caseId } = useParams();
  const { data, isLoading } = useSingleMedicalCase(caseId);

  const form = useForm({
    initialValues: {
      diagnosis: null,
      prioritized: false,
    },
  });

  const diagnosis = useStore(form.store, (state) => state.values.diagnosis);
  const prioritized = useStore(form.store, (state) => state.values.prioritized);

  const diagnosisQuery = useDiagnosisListCached();

  const [requirementList, setRequirementList] = useState([]);
  const [editValues, setEditValues] = useState(null);
  const [selectedMedicalCenter, setSelectedMedicalCenter] = useState(null);
  const [showRequirementModal, setShowRequirementModal] = useState(false);

  const toast = useContext(ToastContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const updateCaseMutation = useMutation({
    mutationFn: updateCase,
    onSuccess: ({ type, message }) => {
      if (type === "error") {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: message,
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["medicalCase", Number(caseId)],
        });
        toast.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Caso médico actualizado",
        });
        navigate(`/casos/${caseId}`);
      }
    },
  });

  return (
    <Dashboard
      headerTitle="Asignar solicitud"
      dynamicGoBack
      panelClassName="w-12 md:w-9 lg:w-7"
    >
      <div className="p-fluid">
        {isLoading ? (
          <ProgressSpinner />
        ) : (
          <form>
            <div>
              {/* SELECCIÓN DE DIAGNÓSTICO */}
              <FormSubHeading title="Diagnóstico" />
              <form.Field name="diagnosis">
                {(field) => (
                  <div className="p-field">
                    <label htmlFor="diagnosis">Diagnóstico</label>
                    <MultiSelect
                      id="diagnosis"
                      name="diagnosis"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.value)}
                      options={diagnosisQuery.data ?? []}
                      optionValue="id"
                      optionLabel="name"
                      placeholder="Seleccionar diagnóstico"
                      filter
                      loading={diagnosisQuery.isLoading}
                      disabled={diagnosisQuery.isLoading}
                      virtualScrollerOptions={{
                        itemSize: 48,
                        style: { width: "650px" },
                      }}
                      showSelectAll={false}
                    />
                  </div>
                )}
              </form.Field>

              <FormSubHeading title="Caso Priorizado" />
              <form.Field name="prioritized">
                {(field) => (
                  <div className="p-field flex gap-2 align-items-center mb-2">
                    <Checkbox
                      inputId="prioritized"
                      name="prioritized"
                      checked={field.state.value}
                      onChange={(e) => field.handleChange(e.checked)}
                    />
                    <label htmlFor="prioritized">Priorizar caso</label>
                  </div>
                )}
              </form.Field>

              <FormSubHeading title="Requerimientos" />
              <RequirementDataView
                requirements={requirementList}
                setRequirementList={setRequirementList}
                setCenterSelected={setSelectedMedicalCenter}
              />

              <Button
                id="btn-add-requirement"
                type="button"
                icon="pi pi-plus"
                label="Añadir requerimiento"
                className="mt-2 mb-2"
                severity="info"
                tooltipOptions={{ showOnDisabled: true, position: "top" }}
                onClick={() => {
                  setEditValues(null);
                  setShowRequirementModal(true);
                }}
              />

              <RequirementModal
                showModal={showRequirementModal}
                onHide={() => {
                  if (!showRequirementModal) return;
                  setEditValues(null);
                  setShowRequirementModal(false);
                }}
                state={data?.state}
                setRequirementList={setRequirementList}
                setInputsLocked={() => {}}
                initialValues={editValues}
                patientClient={data?.company_client_id}
                centerSelected={selectedMedicalCenter}
                setCenterSelected={setSelectedMedicalCenter}
              />
            </div>

            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit]) => (
                <div className="flex gap-2 justify-between mt-2">
                  <Button
                    id="btn-save-case"
                    type="button"
                    icon="pi pi-save"
                    label="Guardar Caso"
                    tooltip="Complete los datos"
                    tooltipOptions={{ position: "top" }}
                    className=""
                    iconPos="right"
                    disabled={!canSubmit || updateCaseMutation.isPending}
                    loading={updateCaseMutation.isPending}
                    onClick={() => {
                      updateCaseMutation.mutate({
                        caseId: Number(caseId),
                        diagnosis,
                        prioritized,
                        requirements: requirementList,
                        medicalCenter: selectedMedicalCenter,
                        status: CaseStatus.PENDIENTE,
                      });
                    }}
                  />
                </div>
              )}
            />
          </form>
        )}
      </div>
    </Dashboard>
  );
}
